import React, { useState, useEffect } from 'react';
import Timeline from './Timeline';
import DateRangesReport from './DateRangesReport';
import '../../Analysis_A/TextAnomalityAnalysis.scss';
import { getAnalyzeActAPIUrl, getAPIUrl } from "../../api/apiUtils";
import { apiFetch } from "../../api/apiClient";

const API_BASE_URL = getAPIUrl();

const TextAnomalityAnalysis = () => {
    const [data, setData] = useState([]);
    const [communities, setCommunities] = useState([]);
    const [allCommunities, setAllCommunities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dateRange, setDateRange] = useState({
        start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        end: new Date().toISOString().split('T')[0]
    });
    const [selectedCommunities, setSelectedCommunities] = useState([]);
    const [hoveredItem, setHoveredItem] = useState(null);

    useEffect(() => {
        fetchTextAnomalityData();
    }, [dateRange, selectedCommunities]);

    const fetchTextAnomalityData = async () => {
        try {
            setLoading(true);
            setError(null);
            const params = new URLSearchParams({
                start_date: dateRange.start,
                end_date: dateRange.end
            });
            selectedCommunities.forEach(id => params.append('group_id', id));
            const response = await apiFetch(`${API_BASE_URL}/monitoring/text-anomality-coverage?${params}`);
            if (!response.ok) throw new Error('Не удалось загрузить данные');
            const result = await response.json();
            setData(result.data || []);
            setCommunities(result.communities || []);
            setAllCommunities(result.all_communities || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const generateDateRange = () => {
        const dates = [];
        const start = new Date(dateRange.start);
        const end = new Date(dateRange.end);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1))
            dates.push(new Date(d).toISOString().split('T')[0]);
        return dates;
    };

    const getDayStatus = (groupId, date) => {
        const dateTimestamp = Math.floor(new Date(date).getTime() / 1000);
        const dayData = data.filter(i => i.group_id === groupId && i.date === dateTimestamp);
        if (dayData.length === 0) return 'empty';
        if (dayData.length === 1) return 'single';
        return 'duplicate';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'empty': return 'bg-gray';
            case 'single': return 'bg-green';
            case 'duplicate': return 'bg-red';
            default: return 'bg-gray';
        }
    };

    const getStatusTooltip = (groupId, date) => {
        const dateTimestamp = Math.floor(new Date(date).getTime() / 1000);
        const dayData = data.filter(i => i.group_id === groupId && i.date === dateTimestamp);
        if (dayData.length === 0) return `Нет данных для ${date}`;
        const uniqueSessions = [...new Set(dayData.map(i => i.session_id))];
        return `
            Дата: ${date}
            Записей: ${dayData.length}
            Уникальных session_id: ${uniqueSessions.length}
            ${dayData.length > 1 ? '⚠️ Обнаружены дубли' : ''}
        `;
    };

    const dateRangeOptions = [
        { label: '7 дней', days: 7 },
        { label: '30 дней', days: 30 },
        { label: '90 дней', days: 90 },
        { label: '180 дней', days: 180 },
        { label: '365 дней', days: 365 }
    ];

    const handleDateRangeChange = (days) => {
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - days);
        setDateRange({
            start: start.toISOString().split('T')[0],
            end: end.toISOString().split('T')[0]
        });
    };

    const dates = generateDateRange();

    return (
        <div className="anomality-analysis">
            <div className="analysis-header">
                <h2><i className="fas fa-exclamation-triangle"></i> Анализ аномальности дискуссий</h2>
                <p>Визуализация наличия данных по группам и датам</p>
            </div>

            <div className="analysis-controls">
                <div className="control-section">
                    <label>Временной диапазон</label>
                    <div className="button-group">
                        {dateRangeOptions.map(opt => (
                            <button
                                key={opt.days}
                                className={((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24)) === opt.days ? 'active' : ''}
                                onClick={() => handleDateRangeChange(opt.days)}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="control-section">
                    <label>Пользовательский диапазон</label>
                    <div className="date-range">
                        <input type="date" value={dateRange.start}
                            onChange={e => setDateRange(p => ({ ...p, start: e.target.value }))} />
                        <span>—</span>
                        <input type="date" value={dateRange.end}
                            onChange={e => setDateRange(p => ({ ...p, end: e.target.value }))} />
                    </div>
                </div>

                <div className="control-section">
                    <label>Фильтр по группам ({allCommunities.length} групп)</label>
                    <div className="group-list">
                        {allCommunities.map(c => (
                            <label key={c.id}>
                                <input
                                    type="checkbox"
                                    checked={selectedCommunities.includes(c.id)}
                                    onChange={e => {
                                        if (e.target.checked)
                                            setSelectedCommunities(p => [...p, c.id]);
                                        else
                                            setSelectedCommunities(p => p.filter(id => id !== c.id));
                                    }}
                                />
                                <span>{c.name}</span>
                            </label>
                        ))}
                    </div>
                    {selectedCommunities.length > 0 && (
                        <div className="clear-filter" onClick={() => setSelectedCommunities([])}>
                            Очистить фильтр
                        </div>
                    )}
                </div>

                <button className="refresh-button" onClick={fetchTextAnomalityData}>
                    <i className="fas fa-sync-alt"></i> Обновить
                </button>

                <div className="legend">
                    <div><div className="legend-color bg-gray"></div>Нет данных</div>
                    <div><div className="legend-color bg-green"></div>Одно значение</div>
                    <div><div className="legend-color bg-red"></div>Дубли</div>
                </div>
            </div>

            {loading && <div className="loading"><i className="fas fa-circle-notch fa-spin"></i> Загрузка данных...</div>}
            {error && <div className="error"><i className="fas fa-exclamation-triangle"></i> {error}</div>}

            {!loading && !error && (
                <div className="analysis-body">
                    <Timeline
                        communities={communities}
                        dates={dates}
                        data={data}
                        getDayStatus={getDayStatus}
                        getStatusColor={getStatusColor}
                        getStatusTooltip={getStatusTooltip}
                        hoveredItem={hoveredItem}
                        setHoveredItem={setHoveredItem}
                    />

                    <div className="summary">
                        <div className="card"><h4>Всего групп</h4><p>{communities.length}</p></div>
                        <div className="card"><h4>Дней в диапазоне</h4><p>{dates.length}</p></div>
                        <div className="card"><h4>Записей с данными</h4><p>{data.length}</p></div>
                        <div className="card"><h4>Дней с дублями</h4>
                            <p className="red">
                                {(() => {
                                    const d = new Set();
                                    communities.forEach(c => dates.forEach(date => {
                                        if (getDayStatus(c.id, date) === 'duplicate') d.add(date);
                                    }));
                                    return d.size;
                                })()}
                            </p>
                        </div>
                    </div>

                    <div className="ranges">
                        <h3>Отчет по диапазонам дат</h3>
                        <DateRangesReport
                            communities={communities}
                            dates={dates}
                            getDayStatus={getDayStatus}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TextAnomalityAnalysis;
